import { api, el, toast } from "./dom";
import { icon } from "./icons";
import { openModal } from "./modal";
import { download, ib } from "./record";
import { emailChips } from "./recipients";
import { openViewer } from "./viewer";

// The Send sheet (brief 1B-4, mockup sendModal): one sheet for every document, opened from any
// file's preview or download control. Tabs for Email, Text, Fax, Mail, Print and Download (only
// with the Download files switch, brief 1B-9); each file can be previewed in the viewer.
// Email and text send a secure link, never the file; fax adds a letterhead cover sheet; mail goes
// on paper through Lob. The server checks everything again (server/core/send.ts).

type Tab = "Email" | "Text" | "Fax" | "Mail" | "Print" | "Download";
const TABS: [Tab, string][] = [
  ["Email", "mail"],
  ["Text", "comment"],
  ["Fax", "file"],
  ["Mail", "paper-airplane"],
  ["Print", "file-badge"],
  ["Download", "download"],
];

interface Recipient {
  name: string;
  why: string | null;
  email: string | null;
  mobile: string | null;
  fax: string | null;
}
interface Options {
  document: string;
  files: { id: string; name: string; pdf: boolean }[];
  sender: string;
  recipients: Recipient[];
  /** The ways the document's responsible person gets it, in order (brief 1E-15). */
  follow: ("email" | "text" | "fax" | "mail")[];
  defaults: { email: string | null; text: string | null; fax: string | null; mail: string };
  linkWords: string;
  pages: number;
  ready: { email: boolean; text: boolean; fax: boolean; mail: boolean };
  why: Record<"email" | "text" | "fax" | "mail", string | null>;
  returnAddress: string | null;
  canDownload: boolean;
}

export interface SendRequest {
  files: { id: string; name: string }[];
  /** The document's name as the sheet and the Outbox show it ("Service agreement"). */
  document?: string;
  /** The responsibility it's for ("Billing"): that contact comes first. */
  for?: string;
  tab?: Tab;
  /** The Mail tab's address and color to start with (a claim packet: the carrier, in color). */
  mail?: { address?: string; color?: boolean };
  /** After something was sent (the Outbox redraws). */
  onSent?: () => void;
  /** A second secure link in the same email (brief 1E-18: an invoice's visit notes). */
  alsoLink?: { fileIds: string[]; title: string } | null;
}

const DONE: Record<string, [string, string?]> = {
  Email: ["Emailed", "They get a secure link to the file"],
  Text: ["Texted a secure link"],
  Fax: ["Fax sent", "The confirmation updates it in the Outbox"],
  Mail: ["Sent to Lob for printing and mailing", "Tracking will show in Inbox › Outbox"],
};

const field = (id: string, label: string, control: HTMLElement, hint?: string) =>
  el(
    "div",
    { class: "field" },
    el("label", { for: id }, label),
    control,
    hint ? el("span", { class: "hint" }, hint) : null,
  );

const check = (label: string, checked: boolean, disabled = false) => {
  const box = el("input", { type: "checkbox", checked, disabled });
  return { box, el: el("label", {}, box, ` ${label}`) };
};

/**
 * Prints the files in the browser: each opens in a frame and the printer dialog comes up. The
 * bytes come from the file's view address, as the viewer's do (brief 1B-9), never a download.
 */
async function printFiles(files: readonly { id: string; name: string }[]) {
  for (const f of files) {
    const opened = await api<{ url: string }>("POST", `/api/files/${f.id}/view`);
    if (!opened.ok) return toast("That file couldn't be printed");
    const parts: ArrayBuffer[] = [];
    let total = 1;
    for (let n = 0; n < total; n++) {
      const res = await fetch(`${opened.body.url}?part=${n}`, { cache: "no-store" });
      if (!res.ok) return toast("That file couldn't be printed");
      total = Number(res.headers.get("x-file-parts") ?? "1");
      parts.push(await res.arrayBuffer());
    }
    const type = /\.pdf$/i.test(f.name) ? "application/pdf" : "";
    const url = URL.createObjectURL(new Blob(parts, type ? { type } : {}));
    const frame = el("iframe", {
      src: url,
      title: `Print ${f.name}`,
      style: "position:fixed;right:0;bottom:0;width:0;height:0;border:0",
    });
    frame.addEventListener("load", () => {
      try {
        frame.contentWindow?.print();
      } catch {
        window.open(url, "_blank");
      }
      setTimeout(() => {
        frame.remove();
        URL.revokeObjectURL(url);
      }, 60_000);
    });
    document.body.append(frame);
  }
}

/** Opens the Send sheet for one record's files. */
export async function openSend(req: SendRequest) {
  const ids = req.files.map((f) => f.id).join(",");
  const q = new URLSearchParams({ files: ids });
  if (req.for) q.set("for", req.for);
  if (req.document) q.set("document", req.document);
  const res = await api<Options>("GET", `/api/send/options?${q}`);
  if (!res.ok) return toast("This file can't be sent", res.body.error);
  const o = res.body;
  // Brief 1E-15: sending follows every way the responsible person gets it, one after another.
  const TAB_OF = { email: "Email", text: "Text", fax: "Fax", mail: "Mail" } as const;
  const follow: Tab[] = (o.follow ?? []).map((w) => TAB_OF[w]);
  const sentBy = new Set<Tab>();
  let tab: Tab =
    req.tab === "Download" && !o.canDownload
      ? "Email"
      : req.tab && (!follow.length || follow.includes(req.tab))
        ? req.tab
        : (follow[0] ?? req.tab ?? "Email");

  const tabs = el("div", { class: "tabs", role: "tablist", "aria-label": "How to send it" });
  const card = el("section", { class: "card", style: "margin:0" });
  const go = el("button", { type: "button", class: "btn pri" });
  const cancel = el("button", { type: "button", class: "btn" }, "Cancel");
  // Each file can be looked at first, in the viewer (it takes the sheet's place).
  const preview = el(
    "div",
    { class: "rowacts", style: "justify-content:flex-start;flex-wrap:wrap" },
    ...o.files.map((f) =>
      el(
        "span",
        { class: "attchip" },
        icon("file"),
        ` ${f.name} `,
        ib("eye", `View ${f.name}`, () => {
          m.close();
          void openViewer({ file: f, document: req.document });
        }),
      ),
    ),
  );
  const ways =
    req.for && follow.length > 1
      ? el(
          "p",
          { class: "small muted", style: "margin:0" },
          `${req.for} goes by ${follow
            .map((t) => t.toLowerCase())
            .join(", ")
            .replace(/, ([^,]*)$/, " and $1")}.`,
        )
      : null;
  const m = openModal({
    title: "Send",
    sub: o.document,
    body: [preview, ...(ways ? [ways] : []), tabs, card],
    actions: [go, cancel],
  });
  cancel.addEventListener("click", () => m.close());

  let collect: () => Record<string, unknown> = () => ({});

  const people = (key: "email" | "mobile" | "fax") =>
    el(
      "datalist",
      { id: `sd-${key}-list` },
      ...o.recipients
        .filter((r) => r[key])
        .map((r) =>
          el("option", { value: r[key] as string }, `${r.name}${r.why ? ` · ${r.why}` : ""}`),
        ),
    );
  const notReady = (why: string | null) =>
    why ? el("p", { class: "note", role: "alert" }, icon("alert"), " ", why) : null;

  function draw() {
    for (const b of tabs.querySelectorAll("button"))
      b.setAttribute("aria-selected", String(b.dataset.tab === tab));
    m.note.hidden = true;
    go.disabled = false;
    const ch = tab.toLowerCase() as "email" | "text" | "fax" | "mail";
    if (tab === "Email") {
      // One email to several people (brief 1E-15): picked from the contacts or typed, as chips.
      const to = emailChips({
        id: "sd-to",
        label: "To",
        start: o.defaults.email ? [o.defaults.email] : [],
        options: o.recipients
          .filter((r) => r.email)
          .map((r) => ({
            value: r.email as string,
            label: `${r.name}${r.why ? ` · ${r.why}` : ""}`,
          })),
      });
      const msg = el("textarea", { id: "sd-msg" }, "Please find the attached.");
      const link = check(`Send as a secure link (expires in ${o.linkWords})`, true, true);
      card.replaceChildren(
        el(
          "div",
          { class: "form", style: "max-width:none" },
          notReady(o.why.email),
          to.el,
          field("sd-msg", "Message", msg),
          el("div", { class: "ptask" }, link.el),
          req.alsoLink
            ? el(
                "p",
                { class: "small", style: "margin:0 0 4px" },
                icon("link"),
                ` Also its own link: ${req.alsoLink.title}`,
              )
            : null,
          el(
            "p",
            { class: "small muted", style: "margin:0" },
            "They get a link to the file. The Outbox shows each open and can turn the link off.",
          ),
        ),
      );
      collect = () => ({
        to: to.values(),
        message: msg.value === "Please find the attached." ? "" : msg.value,
        ...(req.alsoLink ? { alsoLink: req.alsoLink } : {}),
      });
    } else if (tab === "Text") {
      const to = el("input", {
        id: "sd-to",
        type: "tel",
        value: o.defaults.text ?? "",
        list: "sd-mobile-list",
        placeholder: "(410) 555-0100",
        autocomplete: "off",
      });
      card.replaceChildren(
        el(
          "div",
          { class: "form", style: "max-width:none" },
          notReady(o.why.text),
          field("sd-to", "To (mobile)", to),
          people("mobile"),
          el(
            "p",
            { class: "small muted", style: "margin:0" },
            `The text carries a secure link to the document (expires in ${o.linkWords}). No patient details go in the text.`,
          ),
        ),
      );
      collect = () => ({ to: to.value });
    } else if (tab === "Fax") {
      const to = el("input", {
        id: "sd-to",
        type: "tel",
        value: o.defaults.fax ?? "",
        list: "sd-fax-list",
        placeholder: "(410) 555-0190",
        autocomplete: "off",
      });
      const cover = check("Add a cover sheet with letterhead", true);
      const msg = el("textarea", {
        id: "sd-msg",
        placeholder: "A note for the cover sheet (optional)",
      });
      card.replaceChildren(
        el(
          "div",
          { class: "form", style: "max-width:none" },
          notReady(o.why.fax),
          field("sd-to", "Fax number", to),
          people("fax"),
          el("div", { class: "ptask" }, cover.el),
          field("sd-msg", "Note on the cover sheet", msg),
          el(
            "p",
            { class: "small muted", style: "margin:0" },
            `${o.pages} page${o.pages === 1 ? "" : "s"} plus the cover. The fax confirmation is saved with the record.`,
          ),
        ),
      );
      collect = () => ({ to: to.value, cover: cover.box.checked, message: msg.value });
    } else if (tab === "Mail") {
      const to = el("textarea", { id: "sd-to", rows: "4" }, req.mail?.address || o.defaults.mail);
      const services: [string, string][] = [
        ["first_class", "First class"],
        ["certified", "Certified"],
        ["certified_return_receipt", "Certified, return receipt"],
      ];
      const service = el(
        "fieldset",
        { class: "pills" },
        el("legend", {}, "Service"),
        ...services.map(([v, label], i) =>
          el(
            "label",
            {},
            el("input", { type: "radio", name: "sdc", value: v, checked: i === 0 }),
            label,
          ),
        ),
      );
      const color = check("Color", req.mail?.color ?? false);
      const sides = check("Double-sided", true);
      const envelope = check("Return envelope", false);
      const back = check(
        `Return address: ${o.returnAddress ?? "the business on the letterhead"}`,
        true,
        true,
      );
      const opts = () => ({
        service: service.querySelector<HTMLInputElement>("input:checked")?.value ?? "first_class",
        color: color.box.checked,
        doubleSided: sides.box.checked,
        returnEnvelope: envelope.box.checked,
      });
      card.replaceChildren(
        el(
          "div",
          { class: "form", style: "max-width:none" },
          notReady(o.why.mail),
          field(
            "sd-to",
            "Mail to",
            to,
            "From the record's address (the person's home, or the client's). Name, street, then city, state and ZIP.",
          ),
          service,
          el(
            "div",
            { class: "field" },
            el("span", { class: "label" }, "Options"),
            el("div", { class: "checks" }, color.el, sides.el, envelope.el, back.el),
          ),
          el(
            "p",
            { class: "small muted", style: "margin:0" },
            "Printed and mailed by Lob. Tracking shows in the Outbox and on the record.",
          ),
        ),
      );
      collect = () => ({ address: to.value, options: opts() });
    } else if (tab === "Print") {
      card.replaceChildren(
        el(
          "p",
          { class: "small muted", style: "margin:0" },
          `Opens the printer with ${o.files.map((f) => f.name).join(", ")}.`,
        ),
      );
    } else {
      card.replaceChildren(
        el(
          "p",
          { class: "small muted", style: "margin:0" },
          `Saves ${o.files.map((f) => f.name).join(", ")} to this device.`,
        ),
      );
    }
    go.replaceChildren(
      icon(tab === "Download" ? "download" : tab === "Print" ? "file-badge" : "paper-airplane"),
      ` ${tab === "Mail" ? "Mail it" : tab === "Print" ? "Print" : tab === "Download" ? "Download" : "Send"}`,
    );
    if (ch in o.ready && !o.ready[ch as keyof Options["ready"]]) go.disabled = true;
  }

  for (const [t, i] of TABS) {
    if (t === "Download" && !o.canDownload) continue;
    const b = el("button", { type: "button", role: "tab", "data-tab": t }, icon(i), ` ${t}`);
    b.addEventListener("click", () => {
      tab = t;
      draw();
    });
    tabs.append(b);
  }
  draw();

  go.addEventListener("click", async () => {
    go.disabled = true;
    const files = req.files.map((f) => f.id);
    if (tab === "Print" || tab === "Download") {
      if (tab === "Download") for (const f of req.files) await download(f.id, f.name);
      else await printFiles(req.files);
      await api("POST", "/api/send/record", {
        files,
        how: tab.toLowerCase(),
        document: o.document,
      });
      m.close();
      return toast(tab === "Print" ? "Printed" : "Downloaded", o.document);
    }
    const r = await api<{ sent: unknown }>("POST", "/api/send", {
      channel: tab.toLowerCase(),
      files,
      document: o.document,
      ...collect(),
    });
    go.disabled = false;
    if (!r.ok) {
      m.note.replaceChildren(icon("alert"), " ", r.body.error ?? "That wasn't sent.");
      m.note.hidden = false;
      return;
    }
    const [said, sub] = DONE[tab] ?? ["Sent"];
    toast(said, sub ?? o.document);
    req.onSent?.();
    // The next way the responsible person gets it, if any is left.
    sentBy.add(tab);
    const next = follow.find((t) => !sentBy.has(t));
    if (next) {
      tab = next;
      draw();
      return;
    }
    m.close();
  });
}

/** The Send button that sits beside a file's download control (mockup "Send securely"). */
export const sendButton = (req: SendRequest) =>
  ib(
    "paper-airplane",
    `Send ${req.document ?? req.files[0]?.name ?? "this file"}`,
    () => void openSend(req),
  );
